import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Code, ShoppingCart, Zap, Bot, Box, TrendingUp,
  ArrowRight, ArrowLeft, Check, ChevronDown, ChevronUp,
  Loader2, AlertCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';

// Icon mapping
const iconMap = {
  'code': Code,
  'shopping-cart': ShoppingCart,
  'zap': Zap,
  'bot': Bot,
  'box': Box,
  'trending-up': TrendingUp
};

// Process steps data (can be moved to DB later)
const processSteps = {
  tr: [
    { title: 'Keşif & Analiz', desc: 'İhtiyaçlarınızı ve hedeflerinizi detaylı olarak analiz ediyoruz.' },
    { title: 'Strateji & Planlama', desc: 'Projeniz için en uygun strateji ve yol haritasını belirliyoruz.' },
    { title: 'Tasarım & Geliştirme', desc: 'Modern teknolojilerle çözümünüzü hayata geçiriyoruz.' },
    { title: 'Test & Optimizasyon', desc: 'Kapsamlı testlerle kaliteyi garanti altına alıyoruz.' },
    { title: 'Lansman & Destek', desc: 'Projenizi canlıya alıyor ve sürekli destek sağlıyoruz.' }
  ],
  en: [
    { title: 'Discovery & Analysis', desc: 'We analyze your needs and goals in detail.' },
    { title: 'Strategy & Planning', desc: 'We determine the most suitable strategy and roadmap for your project.' },
    { title: 'Design & Development', desc: 'We bring your solution to life with modern technologies.' },
    { title: 'Testing & Optimization', desc: 'We ensure quality with comprehensive testing.' },
    { title: 'Launch & Support', desc: 'We launch your project and provide ongoing support.' }
  ]
};

// FAQ data per service (can be moved to DB later)
const faqData = {
  'web-app': {
    tr: [
      { q: 'Bir web uygulaması geliştirmek ne kadar sürer?', a: 'Projenin kapsamına bağlı olarak 4-16 hafta arasında değişir. Basit projeler daha kısa sürede tamamlanırken, karmaşık kurumsal uygulamalar daha uzun sürebilir.' },
      { q: 'Hangi teknolojileri kullanıyorsunuz?', a: 'React, Next.js, Node.js, Python, PostgreSQL ve modern bulut teknolojileri (AWS, Google Cloud, Vercel) kullanıyoruz.' },
      { q: 'Mevcut sistemlerimle entegre çalışabilir mi?', a: 'Evet, API entegrasyonları ile mevcut CRM, ERP ve diğer sistemlerinizle sorunsuz çalışacak şekilde geliştiriyoruz.' },
      { q: 'Proje sonrası destek sağlıyor musunuz?', a: 'Evet, tüm projelerimizde bakım ve destek paketleri sunuyoruz. 7/24 teknik destek de mümkündür.' }
    ],
    en: [
      { q: 'How long does it take to develop a web application?', a: 'It varies between 4-16 weeks depending on the scope of the project. Simple projects are completed in a shorter time, while complex enterprise applications may take longer.' },
      { q: 'What technologies do you use?', a: 'We use React, Next.js, Node.js, Python, PostgreSQL and modern cloud technologies (AWS, Google Cloud, Vercel).' },
      { q: 'Can it integrate with my existing systems?', a: 'Yes, we develop with API integrations to work seamlessly with your existing CRM, ERP and other systems.' },
      { q: 'Do you provide post-project support?', a: 'Yes, we offer maintenance and support packages for all our projects. 24/7 technical support is also available.' }
    ]
  },
  'default': {
    tr: [
      { q: 'Proje süreci nasıl işliyor?', a: 'Keşif görüşmesi ile başlayıp, analiz, tasarım, geliştirme ve test aşamalarından geçerek projenizi tamamlıyoruz.' },
      { q: 'Fiyatlandırma nasıl yapılıyor?', a: 'Her proje kendine özgüdür. Ücretsiz keşif görüşmesi sonrası detaylı teklif sunuyoruz.' },
      { q: 'Referanslarınızı görebilir miyim?', a: 'Evet, referanslar sayfamızdan tamamladığımız projeleri inceleyebilirsiniz.' },
      { q: 'Proje sonrası destek var mı?', a: 'Evet, tüm projelerimizde bakım ve destek paketleri sunuyoruz.' }
    ],
    en: [
      { q: 'How does the project process work?', a: 'Starting with a discovery meeting, we complete your project through analysis, design, development and testing stages.' },
      { q: 'How is pricing done?', a: 'Every project is unique. We provide a detailed quote after a free discovery meeting.' },
      { q: 'Can I see your references?', a: 'Yes, you can review our completed projects on the references page.' },
      { q: 'Is there post-project support?', a: 'Yes, we offer maintenance and support packages for all our projects.' }
    ]
  }
};

const SolutionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    loadService();
  }, [slug]);

  const loadService = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        navigate('/solutions');
        return;
      }

      setService(data);
    } catch (err) {
      console.error('Error loading service:', err);
      navigate('/solutions');
    } finally {
      setLoading(false);
    }
  };

  const texts = {
    tr: {
      back: 'Tüm Çözümler',
      whatWeOffer: 'Neler Sunuyoruz',
      howWeWork: 'Nasıl Çalışıyoruz',
      faq: 'Sıkça Sorulan Sorular',
      getQuote: 'Teklif Al',
      step: 'Adım'
    },
    en: {
      back: 'All Solutions',
      whatWeOffer: 'What We Offer',
      howWeWork: 'How We Work',
      faq: 'Frequently Asked Questions',
      getQuote: 'Get Quote',
      step: 'Step'
    }
  };

  const t = texts[language] || texts.tr;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!service) {
    return null;
  }

  const IconComponent = iconMap[service.icon] || Code;
  const title = language === 'tr' ? service.title_tr : service.title_en;
  const description = language === 'tr' ? service.description_tr : service.description_en;
  const content = language === 'tr' ? service.content_tr : service.content_en;
  const features = language === 'tr' ? service.features_tr : service.features_en;
  const steps = processSteps[language] || processSteps.tr;
  const faqs = faqData[slug]?.[language] || faqData.default[language] || faqData.default.tr;

  // Parse features if it's a string
  const featuresList = typeof features === 'string' ? JSON.parse(features) : features;

  // Gradient based on slug
  const gradientMap = {
    'web-app': 'from-blue-500 to-cyan-500',
    'ecommerce': 'from-purple-500 to-pink-500',
    'automation': 'from-green-500 to-emerald-500',
    'ai': 'from-orange-500 to-red-500',
    '3d': 'from-indigo-500 to-purple-500',
    'marketing': 'from-pink-500 to-rose-500'
  };
  const gradient = gradientMap[slug] || 'from-blue-500 to-purple-500';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={`${title} - Dijital Çözümler`}
        description={language === 'tr' ? `${title} çözümümüz ile işinizi büyütün. ${description?.substring(0, 120)}...` : `Grow your business with ${title}. ${description?.substring(0, 120)}...`}
        keywords={`${title}, dijital çözüm, ${slug}, cplus media`}
      />

      {/* Hero Section */}
      <div className={`relative bg-gradient-to-br ${gradient} py-20 overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/solutions"
            className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12">
            <div className="flex-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-4 mb-6"
              >
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl sm:text-5xl font-bold text-white mb-6"
              >
                {title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl text-white/90 mb-8 max-w-2xl"
              >
                {content}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Link
                  to="/portal/new-quote"
                  className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  {t.getQuote}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* What We Offer */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            {t.whatWeOffer}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuresList.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 flex items-start gap-4"
              >
                <div className={`w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Check className="w-5 h-5 text-white" />
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium">{feature}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* How We Work */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            {t.howWeWork}
          </h2>
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200 dark:from-blue-900 dark:via-purple-900 dark:to-pink-900 -translate-y-1/2 z-0" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 relative z-10">
              {steps.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.15 }}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 text-center"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4`}>
                    {idx + 1}
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {step.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            {t.faq}
          </h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full p-6 text-left flex items-center justify-between gap-4"
                >
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {faq.q}
                  </span>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-6 pb-6 pt-0">
                    <p className="text-gray-600 dark:text-gray-400">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className={`bg-gradient-to-r ${gradient} rounded-3xl p-12`}>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {language === 'tr' ? 'Projenize başlayalım' : "Let's start your project"}
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              {language === 'tr'
                ? 'Ücretsiz keşif görüşmesi için bizimle iletişime geçin. İhtiyaçlarınıza özel çözümler sunalım.'
                : 'Contact us for a free discovery meeting. Let us offer solutions tailored to your needs.'}
            </p>
            <Link
              to="/portal/new-quote"
              className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              {t.getQuote}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SolutionDetail;
